/**
 * SERBEST FATURA KALEM HESAPLARI
 *
 * fatura/serbestfatura.vue ve fatura/faturakopya.vue ayni hesaplari ayri
 * ayri tasiyordu. faturakopya eski bir serbestfatura kopyasi oldugu icin
 * zamanla ayrismislardi ve AYNI fatura icin FARKLI tutar uretiyorlardi:
 *
 *   Tutar 100, Iskonto %3.333, KDV %20, Tevkifat 1/2
 *     serbestfatura : Iskonto 3.33    KDV 19.33    Tevkifat 9.66
 *     faturakopya   : Iskonto 3.333   KDV 19.3334  Tevkifat 9.6667
 *
 * Buradaki surum SERBESTFATURA'nin surumudur -- kullanici karari:
 * "serbestfatura'daki degerler dogru, faturakopya bayat".
 *
 * Fonksiyonlar SAF: `this` kullanmiyorlar, girdiyi degistirmiyorlar,
 * sonucu donduruyorlar. Boylece birim testi yazilabiliyor
 * (tests/unit/faturahesap.spec.js) ve iki ekran da ayni sonucu aliyor.
 */

/**
 * 2 haneye yuvarlar -- SQL Server'in ROUND(x, 2) davranisiyla AYNI.
 *
 * SQL Server SIFIRDAN UZAGA yuvarlar (banker's rounding degil):
 *     2.675 -> 2.68    1.005 -> 1.01    0.125 -> 0.13
 *    -2.675 -> -2.68  -1.005 -> -1.01  -0.125 -> -0.13
 *
 * DIKKAT: SQL'in ROUND'u TIPE GORE farkli sonuc verir. Olculdu:
 *     ROUND(2.675, 2)                = 2.68   (decimal/numeric)
 *     ROUND(CAST(2.675 AS float), 2) = 2.67   (ikili gosterim)
 * Referansimiz DECIMAL olan: fatura tutarlari veritabaninda "money"
 * sutunlarinda duruyor ve SSMS'te ROUND(x,2) yazildiginda gorulen de bu.
 *
 * ONDALIK GOSTERIM UZERINDEN CALISIR, IKILI GOSTERIM UZERINDEN DEGIL.
 * Bunun sebebi olculdu: onceki uygulama toFixed ile "gurultu temizleyip"
 * ikili deger uzerinde yuvarliyordu ve 320 bin degerlik olcumde SQL'den
 * SAPIYORDU -- ornegin
 *     1.4549999999999998  -> eski: 1.46   SQL: 1.45
 *     2480.9849999999997  -> eski: 2480.99  SQL: 2480.98
 * cunku toFixed once sayiyi tam yarim degere itiyor, sonra yukari
 * yuvarliyordu. Iki bagimsiz kumede 7.056 / 58.984 ve 11.230 / 201.089
 * sapma olculdu; 23 degerlik eski test tablosu bu sinifi goremiyordu.
 *
 * Number.toString() sayinin EN KISA ondalik gosterimidir; JSON'a yazilan
 * ve API'ye ulasan hali de tam olarak budur. Yuvarlamayi o gosterim
 * uzerinde yapinca "1.005 aslinda 1.00499999..." sorunu hic ortaya
 * cikmiyor: ucuncu ondalik hanenin 5 olup olmadigina bakmak yetiyor.
 *
 * Dogrulama: 320.073 deger, IKI BAGIMSIZ hakeme karsi -- sqlcmd ile
 * gercek SQL Server ROUND'u ve BigInt ile tam ondalik aritmetik.
 * Her ikisinde de 0 sapma. (tests/unit/faturahesap.spec.js)
 *
 * Hazir bir alternatifi yok, olculdu: Number(x.toFixed(2)) 11.520,
 * Math.round(x*100)/100 ise 12.066 degerde SQL'den sapiyor.
 *
 * Sayiya cevrilemeyen deger oldugu gibi geri doner (kaynaktaki davranis).
 */
export function yuvarla(deger, hane) {
  var basamak = (hane === undefined || hane === null) ? 2 : hane;

  var sayi = parseFloat(deger);
  if (isNaN(sayi)) return deger;

  var isaret = sayi < 0 ? -1 : 1;
  var metin = Math.abs(sayi).toString();
  var carpan = Math.pow(10, basamak);

  // Ussel gosterim: |x| < 1e-6 ya da |x| >= 1e21. Fatura tutarlarinda
  // gorulmez; kucukse 0, buyukse zaten tam sayidir.
  if (metin.indexOf("e") >= 0) {
    return Math.abs(sayi) < 0.5 / carpan ? 0 : sayi;
  }

  var nokta = metin.indexOf(".");
  // Zaten istenen hane ya da daha az: dokunma (yuvarlama gereksiz islem
  // degil, bolme kisa ondalikli degerleri bile bozabiliyor).
  if (nokta < 0 || metin.length - nokta - 1 <= basamak) return sayi;

  var tamKisim = metin.slice(0, nokta);
  var ondalik = metin.slice(nokta + 1);

  // Istenen kadar hane sayiya katilir; BIR SONRAKI hane 5 ve uzeriyse
  // yukari. Tek haneye bakmak yeterli: '5' ise kalan >= yarim birim,
  // '4' ise arkasindan 9 gelse bile altinda kalir.
  var olcekli = Number(tamKisim + ondalik.slice(0, basamak));
  if (ondalik.charCodeAt(basamak) >= 53) olcekli = olcekli + 1;

  var sonuc = (isaret * olcekli) / carpan;
  return sonuc === 0 ? 0 : sonuc;   // -0 dondurmemek icin
}

/** 2 haneye yuvarlar -- tutarlar icin (money sutunlari, SQL ROUND(x,2)). */
export function round2(deger) {
  return yuvarla(deger, 2);
}

/**
 * 4 haneye yuvarlar -- BIRIM FIYAT icin.
 *
 * Veritabanindaki `AlisSatis.birimfiyat` sutunu `money`; tanimi geregi
 * 4 ondalik tutuyor ve atamada SIFIRDAN UZAGA yuvarliyor. Varsayilmadi,
 * olculdu:
 *     12.34565 -> 12.3457      -12.34565 -> -12.3457
 *     12.34575 -> 12.3458       0.00005  ->   0.0001
 * Bu fonksiyon ayni sonucu veriyor; ekrandan giden deger ile kayitli
 * deger birbirini tutuyor.
 */
export function round4(deger) {
  return yuvarla(deger, 4);
}

/**
 * a x b / 10^us -- TAM ONDALIK carpimla, 2 haneye (SQL ROUND gibi).
 *
 * NEDEN: round2(a * b) carpimi double'da yapiyor; tam yarim kurusa denk
 * gelen bir carpim double'da yarimin ALTINA dusup asagi yuvarlaniyor.
 * Olculdu (2 haneli 2 milyon deger):
 *     350,00 USD x 41,9871 = 14.695,485   double 14695.484999999999
 *         round2(double) 14.695,48   dogrusu 14.695,49     58 sapma
 *     kur 35,5 ile                                     73.539 sapma
 *     0,35 x 0,70 (tevkifat) = 0,245      double 0.24499999999999997
 *         round2(double) 0,24        dogrusu 0,25      82.616 sapma
 * SQL Server ayni carpimi decimal'de yapip dogru degeri veriyor.
 *
 * Iki sayi ondalik gosterimlerinden (toString) tam sayiya cevrilip
 * carpiliyor. Carpim guvenli tam sayi araligini asarsa double carpima geri
 * dusulur -- fatura tutarlarinda gorulmez.
 */
export function carpimYuvarla2(a, b, us) {
  var bolenUs = us || 0;
  var x = ondalikParcala(a);
  var y = ondalikParcala(b);

  var carpim = x && y ? x.tam * y.tam : NaN;
  var olcek = x && y ? x.olcek + y.olcek + bolenUs : 0;

  if (!Number.isSafeInteger(carpim) || olcek - 2 > 15) {
    return round2(parseFloat(a) * parseFloat(b) / Math.pow(10, bolenUs));
  }
  if (olcek <= 2) return round2(carpim / Math.pow(10, olcek));

  var bolen = Math.pow(10, olcek - 2);
  var mutlak = Math.abs(carpim);
  var kalan = mutlak % bolen;
  var bolum = (mutlak - kalan) / bolen;
  if (kalan * 2 >= bolen) bolum = bolum + 1;   // yarim ve uzeri: sifirdan uzaga

  var sonuc = (carpim < 0 ? -bolum : bolum) / 100;
  return sonuc === 0 ? 0 : sonuc;
}

/** Sayinin ondalik gosterimi -> { tam, olcek }; ussel/guvensizse null. */
function ondalikParcala(deger) {
  var sayi = parseFloat(deger);
  if (isNaN(sayi)) return null;

  var metin = Math.abs(sayi).toString();
  if (metin.indexOf("e") >= 0) return null;

  var nokta = metin.indexOf(".");
  var tam = Number(nokta < 0 ? metin : metin.slice(0, nokta) + metin.slice(nokta + 1));
  if (!Number.isSafeInteger(tam)) return null;

  return { tam: sayi < 0 ? -tam : tam, olcek: nokta < 0 ? 0 : metin.length - nokta - 1 };
}

/**
 * Oran alanlari (iskonto %, KDV %, tevkifat orani): bos/NaN -> 0, float
 * gurultusu temizlenir. Veritabanindaki float sutunlar girilen degeri
 * geri vermiyor -- olculdu (AlisSatis): iskyuzde 3.333 -> 3.3329999446868896,
 * TevkifatOran 0.2 -> 0.20000000298023224. Temizlenmezse yeniden yuklenen
 * faturada tutar yarim kurus sinirinda farkli yuvarlanabilir.
 */
function oranTemizle(deger, hane) {
  var sayi = parseFloat(deger);
  return isNaN(sayi) ? 0 : yuvarla(sayi, hane);
}

/**
 * Iskonto tutari -- round2 ile, yani SQL ROUND(x,2) gibi.
 *
 * ONCEDEN ASAGI KIRPIYORDU: ondalik 2 haneden uzunsa Math.floor,
 * degilse sayiya hic dokunmuyordu (3.339 -> 3.33). Diger tutarlar
 * yuvarlanirken iskontonun kirpilmasi, satir toplami ile basligin
 * tutmamasina yol aciyordu. Artik hepsi ayni kurali kullaniyor.
 */
export function iskontoHesapla(detay) {
  return carpimYuvarla2(detay.Tutar, detay.IskontoYuzde, 2);
}

/**
 * KDV tutari. Iskonto DUSULMUS net tutar uzerinden hesaplanir.
 *
 * KdvTip "H" (hariç) : KDV net tutarin uzerine eklenir
 * digeri    (dahil)  : KDV net tutarin ICINDEN ayristirilir
 *
 * 2 haneye yuvarlanir (faturakopya 4 haneye yuvarliyordu).
 */
export function kdvHesapla(detay) {
  // Cikarmanin double gurultusu (94.89999999999999) tam carpimi
  // guvensiz yapmasin diye temizlenir; 2 haneli degere dokunmaz.
  var netTutar = yuvarla(detay.Tutar - detay.IskontoTutar, 8);

  if (detay.KdvTip === "H") {
    return carpimYuvarla2(netTutar, detay.KdvYuzde, 2);
  }
  return round2(netTutar - netTutar / (1 + detay.KdvYuzde / 100));
}

/**
 * Tevkifat tutari: KDV tutarinin oranla carpimi, 2 haneye yuvarlanmis
 * (tam ondalik carpim; bkz. carpimYuvarla2 -- 0,35 x 0,7 = 0,25).
 */
export function tevkifatHesapla(detay) {
  return carpimYuvarla2(detay.KdvTutar, detay.TevkifatDetayOran);
}

/**
 * Fatura toplamlari -- SATIRLARDAN SIFIRDAN hesaplanir.
 *
 * NEDEN SIFIRDAN: kaynak ekranlarda satir ekleme/silme/guncelleme
 * toplamlari ELLE ve ARTIMLI duzeltiyordu
 * (`Tutarlar.ToplamTutar -= yedeksatirkayit.Tutar` gibi, uc metotta 62
 * referans). Bir satir iki kez dusulurse ya da hic dusulmezse toplam
 * sessizce yanlis kaliyor ve bunu fark etmenin yolu yok. Satirlardan
 * yeniden hesaplamak bu hata sinifini tumden kaldiriyor.
 *
 * Sayiya cevrilemeyen alanlar 0 sayilir (kaynaktaki davranis).
 */
export function faturaToplamlari(kalemler) {
  var toplam = {
    ToplamTutar: 0,
    Kdv: 0,
    Iskonto: 0,
    TevkifatKdv: 0,
    FaturaToplam: 0
  };

  if (!Array.isArray(kalemler)) return toplam;

  for (var i = 0; i < kalemler.length; i++) {
    var satir = kalemler[i];

    var tutar = parseFloat(satir.Tutar);
    var kdv = parseFloat(satir.KdvTutar);
    var iskonto = parseFloat(satir.IskontoTutar);
    var tevkifat = parseFloat(satir.TevkifatDetayTutar);

    if (isNaN(tutar)) tutar = 0;
    if (isNaN(kdv)) kdv = 0;
    if (isNaN(iskonto)) iskonto = 0;
    if (isNaN(tevkifat)) tevkifat = 0;

    toplam.ToplamTutar += tutar;
    toplam.Kdv += kdv;
    toplam.Iskonto += iskonto;
    toplam.TevkifatKdv += tevkifat;

    // KdvTip "H" ise Tutar KDV HARICtir, KDV ayrica eklenir.
    // Degilse Tutar KDV'yi zaten iceriyor.
    if (satir.KdvTip === "H") {
      toplam.FaturaToplam += tutar + kdv;
    } else {
      toplam.FaturaToplam += tutar;
    }
  }

  // Genel toplamdan iskonto ve tevkifat DUSULUR (iki ekranin da
  // faturatoplambul'undaki formul).
  toplam.FaturaToplam = toplam.FaturaToplam - toplam.Iskonto - toplam.TevkifatKdv;

  // Kayan nokta birikimini kapat: 0.1 + 0.2 gibi toplamlar aksi halde
  // 0.30000000000000004 kalir ve baslik ile satirlar tutmaz.
  toplam.ToplamTutar = round2(toplam.ToplamTutar);
  toplam.Kdv = round2(toplam.Kdv);
  toplam.Iskonto = round2(toplam.Iskonto);
  toplam.TevkifatKdv = round2(toplam.TevkifatKdv);
  toplam.FaturaToplam = round2(toplam.FaturaToplam);

  return toplam;
}

/**
 * Dovizli faturada satir tutari.
 *
 * Miktar x BirimFiyat doviz tutarini verir; TL tutari kurla carpilarak
 * bulunur. Miktar girilmemisse tutar 0'dir (kaynaktaki davranis).
 */
export function dovizliSatirTutari(detay, kur, dovizli) {
  if (!(detay.Miktar > 0 && detay.BirimFiyat >= 0)) {
    return { DovizTutar: detay.DovizTutar, Tutar: 0 };
  }

  var dovizTutar = detay.Miktar * detay.BirimFiyat;

  if (kur != null && kur != 0 && dovizli === true) {
    return { DovizTutar: dovizTutar, Tutar: dovizTutar * kur };
  }

  return { DovizTutar: dovizTutar, Tutar: detay.Miktar * detay.BirimFiyat };
}


/**
 * SATIRIN TUM TUTARLARI -- BAGIMLILIK SIRASIYLA.
 *
 * Zincir:  Tutar -> IskontoTutar -> KdvTutar -> TevkifatDetayTutar
 *
 * NEDEN GEREKLI: ekranda her alanin kendi izleyicisi vardi ve yalnizca
 * KENDI hesabini yapiyordu. Zincirin gerisi bayat kaliyordu; canli
 * olcum (2026-09-12):
 *
 *   Tutar 300, KDV %20 haric  -> KDV 60
 *   Iskonto %10 girilince     -> Iskonto 30, KDV 60 KALDI   (dogrusu 54)
 *   Miktar 2->3 (Tutar 450)   -> Iskonto 30 KALDI           (dogrusu 45)
 *                                KDV 84 (bayat iskontodan)  (dogrusu 81)
 *   KDV %20->%10              -> Tevkifat 27 KALDI          (dogrusu 13.5)
 *
 * Formuller dogruydu, eksik olan sadece tetiklenme sirasiydi. Burada
 * sira tek yerde toplaniyor; izleyiciler bu fonksiyonu cagiriyor.
 *
 * Girdiyi DEGISTIRMEZ; hesaplanan uc alani dondurur.
 */
export function satirHesapla(detay) {
  // Oran alanlari ekranda bos ('' / null) olabiliyor; sayiya
  // cevrilemeyen oran tutari NaN yapar ve NaN basliga tasinir.
  // Veritabanindan gelen float gurultusu de burada temizlenir (oranTemizle).
  var iskontoTutar = iskontoHesapla({
    Tutar: detay.Tutar,
    IskontoYuzde: oranTemizle(detay.IskontoYuzde, 4)
  });

  var kdvTutar = kdvHesapla({
    Tutar: detay.Tutar,
    IskontoTutar: iskontoTutar,
    KdvTip: detay.KdvTip,
    KdvYuzde: oranTemizle(detay.KdvYuzde, 2)
  });

  // Oran secilmemis satirda ('' / null / undefined) tevkifat yoktur.
  var tevkifatTutar = tevkifatHesapla({
    KdvTutar: kdvTutar,
    TevkifatDetayOran: oranTemizle(detay.TevkifatDetayOran, 4)
  });

  return {
    IskontoTutar: iskontoTutar,
    KdvTutar: kdvTutar,
    TevkifatDetayTutar: tevkifatTutar
  };
}

/**
 * API'YE GONDERILECEK TUTARLAR -- SOZLESME CEVIRISI.
 *
 * Ekranda `ToplamTutar` = satirlarin Tutar toplami (mal/hizmet toplami).
 * API ise ayni alani KDV DAHIL genel toplam sayiyor; kayit sirasinda
 * veritabanindaki brut sutununu sundan hesapliyor
 * (SerbestFaturaSaveV2.cs:1265):
 *
 *     brut = ToplamTutar - Kdv + Iskonto + Tevkifat
 *
 * brut'un anlami mevcut uretim verisinden OLCULDU, varsayilmadi --
 * baska yollarla yazilmis kayitlarda her zaman `brut = tutar - kdv`:
 *     DK/771666  tutar 831.60  kdv 61.59  brut 770.01
 *     CAM/111    tutar  10.00  kdv  0.91  brut   9.09
 *     GGG/1234   tutar  10.00  kdv  1.67  brut   8.33
 * Yani brut = net mal/hizmet toplami.
 *
 * Ekrandaki net degeri gondermek brut'u KDV kadar eksik yaziyordu
 * (450 yerine 414 olctuk). Dogru deger FaturaToplam: dort senaryoda da
 * (iskontosuz, iskonto+tevkifatli, KDV dahil satir, karisik) brut'u
 * dogru veren tek aday budur -- KDV dahil satirda `Tutar` KDV'yi zaten
 * icerdigi icin "satir toplami" ile "net" ayni sey degildir.
 *
 * Ekrandaki deger DEGISMIYOR: bu cevirim yalnizca gonderilen KOPYA
 * uzerinde yapiliyor, boylece ekranda `ToplamTutar` satirlarin
 * toplamina esit kalmaya devam ediyor.
 */
export function apiIcinTutarlar(tutarlar) {
  if (!tutarlar) return tutarlar;

  return {
    ToplamTutar: tutarlar.FaturaToplam,
    Iskonto: tutarlar.Iskonto,
    FaturaToplam: tutarlar.FaturaToplam,
    Kdv: tutarlar.Kdv,
    TevkifatKdv: tutarlar.TevkifatKdv
  };
}

/**
 * Faturanin API'ye gonderilecek hali: SIG kopya, yalnizca Tutarlar
 * degistirilmis. Canli nesne bozulmasin diye kopya sart -- `Tutarlar`
 * ekrana da bagli.
 */
export function apiIcinFatura(fatura) {
  if (!fatura) return fatura;

  var kopya = {};
  for (var alan in fatura) {
    if (Object.prototype.hasOwnProperty.call(fatura, alan)) kopya[alan] = fatura[alan];
  }
  kopya.Tutarlar = apiIcinTutarlar(fatura.Tutarlar);
  return kopya;
}

/**
 * DOVIZLI FATURADA SATIRIN TUM TUTARLARI -- DOVIZ ESASLI.
 *
 * Dovizli fatura dovizle duzenlenir: belgenin (e-faturanin) rakamlari
 * doviz cinsinden 2 haneli tutarlardir. Iskonto, KDV ve tevkifat DOVIZ
 * tutari uzerinden satirHesapla ile ayni zincirle hesaplanir; TL
 * karsiliklarinin HER BIRI ayri ayri `doviz x kur` ile bulunur.
 *
 * NEDEN: once zincir TL'de kuruluyor, dovize geri donuluyordu. Ayni
 * fatura (E2E/12204729, 30 satir) uc yerde uc farkli genel toplam
 * veriyordu -- olculdu:
 *     ekran ve CariHareket.doviztutar : 142.364,24 USD  (TL toplam / kur)
 *     e-fatura XML (PayableAmount)    : 142.364,23 USD  (SP: TL satir / kur)
 *     doviz esasli (bu fonksiyon)     : 142.364,21 USD
 * E-faturaya ekranda girilen 2 haneli doviz rakamlari degil, TL'den geri
 * cevrilmis 8 haneli degerler gidiyordu (99,90 yerine 99,89996928).
 *
 * AYNI KURAL UC YERDE uygulanir; biri degisirse digerleri de degismeli:
 *   ekran : burasi
 *   API   : SerbestFaturaDovizHesap.cs   (CariHareket.doviztutar)
 *   SP    : E_FaturaGet_V2, serbest fatura dovizli dali   (e-fatura)
 *
 * Girdiyi DEGISTIRMEZ.
 */
export function dovizSatirHesapla(detay, kur) {
  var dovizTutar = round2(parseFloat(detay.DovizTutar));
  if (isNaN(dovizTutar)) dovizTutar = 0;

  var doviz = satirHesapla({
    Tutar: dovizTutar,
    IskontoYuzde: detay.IskontoYuzde,
    KdvTip: detay.KdvTip,
    KdvYuzde: detay.KdvYuzde,
    TevkifatDetayOran: detay.TevkifatDetayOran
  });

  var oran = parseFloat(kur);
  if (isNaN(oran)) oran = 0;

  return {
    DovizTutar: dovizTutar,
    DovizIskonto: doviz.IskontoTutar,
    DovizKdv: doviz.KdvTutar,
    DovizTevkifat: doviz.TevkifatDetayTutar,
    Tutar: carpimYuvarla2(dovizTutar, oran),
    IskontoTutar: carpimYuvarla2(doviz.IskontoTutar, oran),
    KdvTutar: carpimYuvarla2(doviz.KdvTutar, oran),
    TevkifatDetayTutar: carpimYuvarla2(doviz.TevkifatDetayTutar, oran)
  };
}

/**
 * DIP TOPLAMIN DOVIZ KARSILIGI -- satirlarin DOVIZ tutarlarinin toplami.
 *
 * Her satir dovizSatirHesapla ile hesaplanip dovizleri toplanir; TL
 * toplamdan kura bolunerek GERI cevrilmez (nedeni dovizSatirHesapla'da).
 * Genel toplam faturaToplamlari ile ayni formul: KDV haric satirda KDV
 * eklenir, dahil satirda tutar zaten icerir; iskonto ve tevkifat dusulur.
 *
 * Kur yoksa, 0 ise ya da satir listesi yoksa null doner -- cagiran taraf
 * hic gostermez.
 */
export function dovizToplamlari(kalemler, kur) {
  if (!Array.isArray(kalemler) || !(parseFloat(kur) > 0)) return null;

  var t = { ToplamTutar: 0, Iskonto: 0, Kdv: 0, TevkifatKdv: 0, FaturaToplam: 0 };

  for (var i = 0; i < kalemler.length; i++) {
    var s = dovizSatirHesapla(kalemler[i], kur);

    t.ToplamTutar += s.DovizTutar;
    t.Iskonto += s.DovizIskonto;
    t.Kdv += s.DovizKdv;
    t.TevkifatKdv += s.DovizTevkifat;
    t.FaturaToplam += kalemler[i].KdvTip === "H" ? s.DovizTutar + s.DovizKdv : s.DovizTutar;
  }

  t.FaturaToplam = t.FaturaToplam - t.Iskonto - t.TevkifatKdv;

  t.ToplamTutar = round2(t.ToplamTutar);
  t.Iskonto = round2(t.Iskonto);
  t.Kdv = round2(t.Kdv);
  t.TevkifatKdv = round2(t.TevkifatKdv);
  t.FaturaToplam = round2(t.FaturaToplam);

  return t;
}

/**
 * Satir tutari ELLE girildiginde birim fiyat.
 *
 * Kaynak, dovizli faturada DOVIZ tutari, dovizsizde TL tutaridir (birim
 * fiyat alani da hangi para biriminde ise o).
 *
 * Miktar x BirimFiyat = DovizTutar bagintisi korunsun diye birim fiyat
 * dovizden geri turetilir. Turetilemiyorsa (miktar yok/0) null doner;
 * cagiran taraf birim fiyata dokunmaz.
 *
 * YUVARLANMAZ. Yuvarlansaydi (orn. 100/3 -> 33,33) kullanici Miktar'a bir
 * sonraki dokunusunda dovizlitutarhesapla() 3 x 33,33 = 99,99 yazar ve
 * girilen 100 sessizce kayardi. Ekranda zaten 2 hane gosteriliyor
 * (parainput); model tam degeri tutar.
 */
export function birimFiyatTuret(dovizTutar, miktar) {
  var tutar = parseFloat(dovizTutar);
  var adet = parseFloat(miktar);

  if (isNaN(tutar) || isNaN(adet) || adet <= 0) return null;

  return tutar / adet;
}

export default {
  yuvarla: yuvarla,
  round2: round2,
  round4: round4,
  carpimYuvarla2: carpimYuvarla2,
  birimFiyatTuret: birimFiyatTuret,
  dovizSatirHesapla: dovizSatirHesapla,
  dovizToplamlari: dovizToplamlari,
  apiIcinTutarlar: apiIcinTutarlar,
  apiIcinFatura: apiIcinFatura,
  satirHesapla: satirHesapla,
  iskontoHesapla: iskontoHesapla,
  kdvHesapla: kdvHesapla,
  tevkifatHesapla: tevkifatHesapla,
  faturaToplamlari: faturaToplamlari,
  dovizliSatirTutari: dovizliSatirTutari
};
